import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useToast } from "components/Toast";
import { ArrowLeft, Save } from "lucide-react";

export default function RerunnerCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    clientId: "",
    clientName: "",
    uniqueCompanyName: "",
    timeWindowHours: 24,
    retriableDeclineCodes: "",
    excludedDeclineCodes: "",
    minAmount: "",
    maxAmount: "",
    requestsPerSecond: 2,
    maxConcurrent: 1,
    schedule: "",
    baseUrl: "",
  });

  function onChange(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);

    const retriable = form.retriableDeclineCodes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (!retriable.length) {
      toast({ status: "warning", title: "Validation", description: "At least one retriable decline code is required." });
      setSaving(false);
      return;
    }

    const excluded = form.excludedDeclineCodes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const filters = {};
    if (form.minAmount !== "") filters.minAmount = Number(form.minAmount);
    if (form.maxAmount !== "") filters.maxAmount = Number(form.maxAmount);

    const payload = {
      clientId: form.clientId,
      clientName: form.clientName,
      uniqueCompanyName: form.uniqueCompanyName,
      timeWindowHours: Number(form.timeWindowHours),
      retriableDeclineCodes: retriable,
      rateLimit: {
        requestsPerSecond: Number(form.requestsPerSecond),
        maxConcurrent: Number(form.maxConcurrent),
      },
    };

    if (excluded.length) payload.excludedDeclineCodes = excluded;
    if (Object.keys(filters).length) payload.filters = filters;
    if (form.schedule) payload.schedule = form.schedule;
    if (form.baseUrl) payload.baseUrl = form.baseUrl;

    try {
      await makeApiRequest(
        "post",
        apiRoutes.createRerunnerConfig,
        { id: user.id, accessToken: user.accessToken },
        companyId,
        payload
      );
      toast({ status: "success", title: "Config created", description: `${form.clientName} has been created.` });
      navigate("/rerunner");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error || "Something went wrong.";
      if (status === 409) {
        toast({ status: "warning", title: "Conflict", description: msg });
      } else {
        toast({ status: "destructive", title: "Create failed", description: msg });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Create Rerunner Config"
        actions={
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={() => navigate("/rerunner")}>
              <ArrowLeft className="h-4 w-4" />
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              <Save className="h-4 w-4" />
              {saving ? "Creating..." : "Create Config"}
            </button>
          </div>
        }
      />

      <form onSubmit={handleSubmit} className="container py-6 space-y-6">
        {/* Identity */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Identity</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Client ID</label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="e.g. cleansupplements"
                value={form.clientId}
                onChange={onChange("clientId")}
                required
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Client Name</label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="e.g. Clean Supplements"
                value={form.clientName}
                onChange={onChange("clientName")}
                required
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Company Name</label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="uniqueCompanyName from etlCore"
                value={form.uniqueCompanyName}
                onChange={onChange("uniqueCompanyName")}
                required
              />
            </div>
          </div>
        </section>

        {/* Decline Codes */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Decline Codes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Retriable Codes <span className="text-destructive">*</span>
              </label>
              <textarea
                className="input input-bordered w-full min-h-[80px]"
                placeholder="Comma-separated, e.g. 200, 201, 300"
                value={form.retriableDeclineCodes}
                onChange={onChange("retriableDeclineCodes")}
                required
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Excluded Codes</label>
              <textarea
                className="input input-bordered w-full min-h-[80px]"
                placeholder="Comma-separated (optional)"
                value={form.excludedDeclineCodes}
                onChange={onChange("excludedDeclineCodes")}
              />
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Min Amount ($)</label>
              <input
                type="number"
                className="input input-bordered w-full"
                placeholder="0"
                value={form.minAmount}
                onChange={onChange("minAmount")}
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Max Amount ($)</label>
              <input
                type="number"
                className="input input-bordered w-full"
                placeholder="No limit"
                value={form.maxAmount}
                onChange={onChange("maxAmount")}
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </section>

        {/* Rate Limiting */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Rate Limiting</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Requests/sec</label>
              <input
                type="number"
                className="input input-bordered w-full"
                value={form.requestsPerSecond}
                onChange={onChange("requestsPerSecond")}
                min="1"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Max Concurrent</label>
              <input
                type="number"
                className="input input-bordered w-full"
                value={form.maxConcurrent}
                onChange={onChange("maxConcurrent")}
                min="1"
              />
            </div>
          </div>
        </section>

        {/* Schedule & Misc */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold">Schedule & Misc</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Time Window (hours)</label>
              <input
                type="number"
                className="input input-bordered w-full"
                value={form.timeWindowHours}
                onChange={onChange("timeWindowHours")}
                min="1"
                required
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Cron Schedule</label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="e.g. 0 */6 * * *"
                value={form.schedule}
                onChange={onChange("schedule")}
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Base URL</label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="CheckoutChamp API URL (optional)"
                value={form.baseUrl}
                onChange={onChange("baseUrl")}
              />
            </div>
          </div>
        </section>
      </form>
    </div>
  );
}
